import { useIntl } from 'react-intl-next';
import { useEffect } from 'react';

import type {
	ForgeUIExtensionQueryOptions,
	UsePreFilteredExtensionsQueryOptions,
} from '@atlassian/forge-ui/provider';
import { usePrefilteredExtensionList as usePrefilteredExtensionListExternal } from '@atlassian/forge-ui/provider';

import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail';
import { isTooManyRequestsError } from '@confluence/error-boundary/entry-points/isTooManyRequestsError';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';

import { getUsePrefilteredExtensionListOptions } from './getUsePrefilteredExtensionListOptions';

export type UsePrefilteredExtensionListProps = {
	moduleType: string;
	queryOptions?: UsePreFilteredExtensionsQueryOptions | ForgeUIExtensionQueryOptions;
	returnBlockedExtensions?: boolean;
	spaceContextOnly?: boolean;
};

/**
 * This hook provides a pre-filtered list of extensions based on the user's context and preferences.
 * Unlike `useFilteredExtensionList`, it does not apply app access rules and display conditions filtering on the frontend
 * after fetching extensions from the backend. All filtering is applied on the backend. This hook simply fetches the
 * pre-filtered list of extensions without any additional processing, does some lightweight transformation on the data to ensure backwards compatibility
 * and returns extensions to the calling component.
 */
export const usePreFilteredExtensionList = ({
	moduleType,
	queryOptions,
	returnBlockedExtensions = false,
	spaceContextOnly,
}: UsePrefilteredExtensionListProps) => {
	const { locale } = useIntl();
	const { cloudId } = useSessionData();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const { error: spaceError } = useSpaceDetail(spaceKey);

	const options = getUsePrefilteredExtensionListOptions({
		cloudId,
		locale,
		moduleType,
		queryOptions,
		includeHidden: returnBlockedExtensions,
		spaceKey,
		contentId,
		spaceContextOnly,
	});

	const {
		extensions: extsData,
		loading: extsLoading,
		error: extsError,
	} = usePrefilteredExtensionListExternal(options);

	useEffect(() => {
		// suppress 403 errors as we can't do much with them
		if (extsError && isUnauthorizedError(extsError)) {
			markErrorAsHandled(extsError);
		}

		// suppress 429 errors for offending users
		if (isTooManyRequestsError(extsError)) {
			markErrorAsHandled(extsError);
		}

		if (spaceError) {
			markErrorAsHandled(spaceError);
		}
	}, [extsError, spaceError]);

	return {
		extensions: extsData,
		loading: extsLoading,
		error: extsError,
	};
};
